import { websocketURL } from "../settings";
import {
    Building, Language, Expert, Premise,
    TimeslotResponse, Models, Operations,
    DataResponse, EntityResponse, WaitingResponses,
    RequestReservationResponseFailure, RequestReservationResponseSuccess, DataStore,
    Contexes,
    Locations,
    Experts,
    CancelReservationFailureResponse,
    CancelReservationSuccessResponse,
    LocationCoords,
    Languages,
    Specialities,
    Speciality,
    Services,
    CampaignService,
    MapBoxTokenResponse,
} from "../types";
import {
    initializeSocket, send, createOnmessage,
    createRequestSessionKey,
    utoa,
    arraysAreEqual,
    tryJSONParse,
    hasData
} from "./socketHelpers";
import moment from "moment";
import {
    timeslotStore
} from "../store/stores";
import api from "./topaasiApi";


const waitingResponses: WaitingResponses<any> = {};

export const requestData = <T>(
    socket: WebSocket,
    filter: string[] = [],
    position?: GeolocationPosition | null,
    ctx?: Contexes
): Promise<DataResponse<T>> => {
    const location = position && {
        lon: position.coords.longitude,
        lat: position.coords.latitude
    };
    // API demands request to be strings with strict ordering
    const msgStr = `{"op":"${Operations.data}",${ctx ? `"ctx":"${ctx}",` : ""}"filter":${JSON.stringify(filter)}${location ? `,"location":{"lon":${location.lon},"lat":${location.lat}}` : ""}}`;
    const msg = JSON.parse(msgStr);

    const matchResponseToCallback = (startTime: number) => (resp: DataResponse<T>) => {
        if (process.env.NODE_ENV === "development") {
            if (ctx === Contexes.palvelu) {
                const endTime = performance.now();
                console.log(`requestData (palvelu) took ${Math.floor(endTime - startTime)} ms`);
            }
        }
        const respObj = tryJSONParse(resp as unknown as string);

        return respObj.op === msg.op &&
            respObj.filter &&
            arraysAreEqual(msg.filter, respObj.filter) &&
            respObj.ctx === ctx;
    };

    return new Promise((resolve, reject) => {
        const callback = (data: DataResponse<T>) => resolve(data);
        send(
            socket,
            callback,
            matchResponseToCallback(performance.now()),
            msg, waitingResponses,
            reject,
            msgStr
        );
    });
};


export const requestDataStoreEntity = <T>(
    socket: WebSocket,
    uuid: string,
    store: DataStore<T>,
    loggingString?: string,
    location?: LocationCoords | null
): Promise<EntityResponse<T>> => {
    // API demands request to be strings with strict ordering
    const msgStr = `{"op":"${Operations.data}","uuid":"${uuid}"${location ? `,"location":{"lon":${location.lon},"lat":${location.lat}}` : ""}}`;
    const msg = JSON.parse(msgStr);
    const matchResponseToCallback = (startTime: number) => (resp: EntityResponse<T>) => {
        if (process.env.NODE_ENV === "development") {
            if (loggingString) {
                const endTime = performance.now();
                console.log(`requestDataStoreEntity (${loggingString}) took ${Math.floor(endTime - startTime)} ms`);
            }
        }
        const respObj = tryJSONParse(resp as unknown as string);
        return respObj.op === msg.op && msg.uuid === respObj.uuid;
    };

    return new Promise(async (resolve, reject) => {
        const callback = async (data: EntityResponse<T>) => {
            try {
                await hasData(data.data as object);
                await store.updateState(store.subject, data);
                resolve(data);
            } catch (err) {
                await store.updateState(store.subject, data);
                reject("Response has no data.");
            }
        };
        const cached = await store.getValueByUuid(uuid);
        cached ?
            resolve(cached) :
            send(
                socket,
                callback,
                matchResponseToCallback(performance.now()),
                msg,
                waitingResponses,
                reject,
                msgStr
            );
    });
};

//Move this where it belongs
export interface TimeSpan {
    from: String,
    to: String
}
const createRequestTimesInRange = (socket: WebSocket) =>
    (
        dateFrom: Date,
        dateTo: Date,
        filter: string[] = [],

    ): Promise<DataResponse<any>> => {
        const from = moment.utc(dateFrom).local().format("YYYY-MM-DD");
        const to = moment.utc(dateTo).local().format("YYYY-MM-DD");

        // API demands request to be strings with strict ordering
        const msgStr = `{"op":"${Operations.appointment}","range":{"from":"${from}","to":"${to}"},"filter":${JSON.stringify(filter)}}`;
        const msg = JSON.parse(msgStr);
        const matchResponseToCallback = (resp: DataResponse<TimeslotResponse[]>) => {
            const respObj = tryJSONParse(resp as unknown as string);
            return respObj.op === msg.op &&
                respObj.range &&
                respObj.range.from === msg.range.from &&
                respObj.range.to === msg.range.to &&
                arraysAreEqual(msg.filter, respObj.filter);
        };

        return new Promise((resolve, reject) => {
            const callback = (data: any) => resolve(data);
            send(
                socket,
                callback,
                matchResponseToCallback,
                msg,
                waitingResponses,
                reject,
                msgStr
            );
        });
    };


const createDataStoreRequestTimesInHourRange = (
    socket: WebSocket
) => (
    date: Date,
    timeSpan: TimeSpan,
    filter: string[] = [],
    from: Number = 0,
    selectedExpertsUuids: string[],
    selectedPremisesUuids: string[],
    selectedServicesUuids: string[],
    limit: number = 30
): Promise<DataResponse<TimeslotResponse[]>> => {
        const formattedDate = moment.utc(date).local().format("YYYY-MM-DD");
        const range = timeSpan.from === "00:00" && timeSpan.to === "23:59" ? "" : `"range":{"from":"${timeSpan.from}Z","to":"${timeSpan.to}Z"},`;
        // API demands request to be strings with strict ordering
        const msgStr = `{"op":"${Operations.appointment}","date":"${formattedDate}",${range}"filter":${JSON.stringify(filter)},"from":${from},"limit":${limit}}`;
        const msg = JSON.parse(msgStr);

        const matchResponseToCallback = (startTime: number) => (
            (resp: DataResponse<TimeslotResponse[]>) => {
                if (process.env.NODE_ENV === "development") {
                    const endTime = performance.now();
                    console.log(`createDataStoreRequestTimesInHourRange took ${Math.floor(endTime - startTime)} ms`);
                }
                const respObj = tryJSONParse(resp as unknown as string);
                return respObj.op === msg.op &&
                    respObj.date === msg.date &&
                    ((respObj.range && respObj.range.from === msg.range.from &&
                        respObj.range.to === msg.range.to) || !respObj.range) &&
                    arraysAreEqual(respObj.filter, msg.filter) &&
                    msg.from === respObj.from;
            }
        );

        return new Promise((resolve, reject) => {
            const callback = async (data: DataResponse<TimeslotResponse[]>) => {
                await timeslotStore.updateState(
                    timeslotStore.subject,
                    data,
                    selectedExpertsUuids,
                    selectedPremisesUuids,
                    selectedServicesUuids,
                    date
                );
                resolve(data);
            };
            send(
                socket,
                callback,
                matchResponseToCallback(performance.now()),
                msg,
                waitingResponses,
                reject,
                msgStr
            );
        });
    };


type ReservationResponses =
    RequestReservationResponseSuccess | RequestReservationResponseFailure;

const createRequestReservation = (
    socket: WebSocket
) => <T>(
    uuid: string,
    ssn: string,
    firstName: string,
    lastName: string,
    phoneNumber: string,
    emailAddress: string,
    streetAddress: string,
    postalCode: string,
    localityName: string,
    dataConsent: boolean,
    marketingConsent: boolean,
    moreInformation: string,
    sendEmailConfirmation: boolean,
    sendSmsConfirmation: number | false,
    serviceVoucherCode: string | null,
    discountCode: string | null
): Promise<ReservationResponses> => {
        // API demands request to be strings with strict ordering
        const consent = `{"personalData":${dataConsent},"marketingData":${marketingConsent}}`;
        const data = `{"ssn":"${ssn}","firstName":"${utoa(firstName)}","lastName":"${utoa(lastName)}","phoneNumber":"${utoa(phoneNumber)}","emailAddress":"${emailAddress.replace(/\s/g, "")}","streetAddress":"${utoa(streetAddress)}","postalCode":"${utoa(postalCode)}","localityName":"${utoa(localityName)}","consent":${consent},"custom1":"${utoa(`${serviceVoucherCode ? `palvelusetelin numero: ${serviceVoucherCode}, ` : ""}${discountCode ? `alennuskoodi: ${discountCode}, ` : ""}${moreInformation}`)}","SendEmailConfirmation":${sendEmailConfirmation},"SendSmsConfirmation":${sendSmsConfirmation}}`;
        const msgStr = `{"op":"${Operations.appointment}","uuid":"${uuid}","data":${data}}`;
        const msg = JSON.parse(msgStr);
        const matchRespnseToCallback = (resp: ReservationResponses) => {
            const respObj = tryJSONParse(resp as unknown as string);
            return respObj.op === msg.op &&
                respObj.uuid === msg.uuid &&
                typeof respObj.success === "boolean";
        };

        return new Promise((resolve, reject) => {
            const callback = (data: ReservationResponses) => resolve(data);
            send(socket, callback, matchRespnseToCallback, msg, waitingResponses, reject, msgStr);
        });
    };

const createRequestReservationCancellation = (socket: WebSocket) => (
    reservationUuid: string,
    pin: string,
    ssn: string,
    ssnRequired: boolean,
)

    : Promise<CancelReservationSuccessResponse | CancelReservationFailureResponse> => {
    //  API demands request to be strings with strict ordering

    const msgStr = `{"op":"${Operations.appointment}","uuid":"${reservationUuid}","data":{"cancellation pin":"${pin}"${ssnRequired ? `,"ssn":"${ssn}"` : ""}}}`;
    console.log(msgStr);
    const msg = JSON.parse(msgStr);


    const matchResponseToCallback = (
        resp: CancelReservationSuccessResponse | CancelReservationFailureResponse
    ) => {
        const respObj = tryJSONParse(resp as unknown as string);

        return respObj.op === msg.op &&
            respObj.uuid === reservationUuid &&
            typeof respObj.success === "boolean";
    };

    return new Promise((resolve, reject) => {
        const callback = (
            data: CancelReservationSuccessResponse | CancelReservationFailureResponse
        ) => resolve(data);

        send(socket, callback, matchResponseToCallback, msg, waitingResponses, reject, msgStr);
    });
};

const createRequestLocations = () => (
    filters: string[],
    position?: GeolocationPosition | null
): Promise<DataResponse<Locations>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getLocations(filters, position);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting locations from API.", e);
            reject();
        }
    });
};

const createRequestExperts = () => (
    filters: string[]
): Promise<DataResponse<Experts>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getExperts(filters);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting experts from API.", e);
            reject();
        }
    });
};


const createRequestSpecialities = () => (
    filters: string[]
): Promise<DataResponse<Specialities>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getSpecialities(filters);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting specialities from API.", e);
            reject();
        }
    });
};


const createRequestLanguages = () => (
    filters: string[]
): Promise<DataResponse<Languages>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getLanguages(filters);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting languages from API.", e);
            reject();
        }
    });
};

const createRequestServices = () => (
    filters: string[]
): Promise<DataResponse<Services>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getServices(filters);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting services from API.", e);
            reject();
        }
    });
};

const createRequestAllServices = () => (
): Promise<DataResponse<CampaignService[]>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getAllServices();
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting service tree from API.", e);
            reject();
        }
    });
};

const createRequestPremise = () => (
    uuid: string
): Promise<EntityResponse<Premise>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getPremise(uuid);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting location from API.", e);
            reject();
        }
    });
};

const createRequestExpert = () => (
    uuid: string
): Promise<EntityResponse<Expert>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getExpert(uuid);
            resolve(apiData);
        } catch (e) {
            console.error("Error when getting expert from API.", e);
            reject();
        }
    });
};

const createRequestSpeciality = () => (
    uuid: string
): Promise<EntityResponse<Speciality>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getSpeciality(uuid);
            resolve(apiData);
        } catch (e) {
            console.error("Error when getting speciality from API.", e);
            reject();
        }
    });
};

const createRequestLanguage = () => (
    uuid: string
): Promise<EntityResponse<Language>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getLanguage(uuid);
            resolve(apiData);
        } catch (e) {
            console.error("Error when getting language from API.", e);
            reject();
        }
    });
};

type ICreatePopulateUuids =
    EntityResponse<Expert | Premise | Language | Speciality> | CampaignService | null

const createPopulateFilters = () =>
    async (uuid: string, services: CampaignService[]): Promise<ICreatePopulateUuids> => {

        const service = services.find(service => service.uuid === uuid);
        if (service) return service;

        let populated = null;

        try {
            populated = await api.getByUuid(uuid);
        } catch (e) {
            populated = null;
            console.error("Error when fetching with uuid from API.", e);
        }

        if (!populated) return null;

        const expert = populated.model === Models.työntekijä ?
            populated as EntityResponse<Expert> :
            null;
        const premise = populated.model === Models.toimipaikka ?
            populated as EntityResponse<Premise> :
            null;
        const language = populated.model === Models.kieli ?
            populated as EntityResponse<Language> :
            null;
        const speciality = populated.model === Models.erityisosaaminen ?
            populated as EntityResponse<Speciality> :
            null;

        return expert || premise || language || speciality || null;
    };

const createRequestBuilding = () => (
    uuid: string
): Promise<EntityResponse<Building>> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getBuilding(uuid);
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting building from API.", e);
            reject();
        }
    });
};

const createRequestMapBoxToken = () => (): Promise<MapBoxTokenResponse> => {
    return new Promise(async (resolve, reject) => {
        try {
            const apiData = await api.getMapboxtoken();
            resolve(apiData);

        } catch (e) {
            console.error("Error when getting services from API.", e);
            reject();
        }
    });
};

export interface IDataService {
    requestServices: (filters: string[]) => Promise<DataResponse<Services>>,
    requestAllServices: () => Promise<DataResponse<CampaignService[]>>,
    requestLocations: (
        filters: string[],
        position?: GeolocationPosition | null
    ) => Promise<DataResponse<Locations>>,
    requestExperts: (
        filters: string[]
    ) => Promise<DataResponse<Experts>>,
    requestExpert: (uuid: string) => Promise<EntityResponse<Expert>>,
    requestPremise: (uuid: string) => Promise<EntityResponse<Premise>>,
    requestTimesInRange: (dateFrom: Date, dateTo: Date, filter: string[]) =>
        Promise<DataResponse<string[]>>,
    requestTimesInHourRange: (
        date: Date,
        timeSpan: TimeSpan,
        filter: string[],
        from: Number,
        selectedExpertsUuids: string[],
        selectedPremisesUuids: string[],
        selectedServicesUuids: string[]
    ) =>
        Promise<DataResponse<TimeslotResponse[]>>,
    requestBuilding: (
        uuid: string
    ) => Promise<EntityResponse<Building>>,
    requestLanguage: (uuid: string) => Promise<EntityResponse<Language>>,
    populateFilters: (uuid: string, services: CampaignService[]) => Promise<ICreatePopulateUuids>,
    requestReservation: (
        uuid: string,
        ssn: string,
        firstName: string,
        lastName: string,
        phoneNumber: string,
        emailAddress: string,
        streetAddress: string,
        postalCode: string,
        localityName: string,
        dataConsent: boolean,
        marketingConsent: boolean,
        moreInformation: string,
        sendEmailConfirmation: boolean,
        sendSmsConfirmation: number | false,
        serviceVoucherCode: string | null,
        discountCode: string | null
    ) => Promise<ReservationResponses>,
    requestReservationCancellation: (
        reservationUuid: string,
        pin: string,
        ssn: string,
        ssnRequired: boolean
    ) => Promise<CancelReservationSuccessResponse | CancelReservationFailureResponse>,
    requestLanguages: (
        filters: string[]
    ) => Promise<DataResponse<Languages>>,
    requestSpeciality: (
        uuid: string
    ) => Promise<EntityResponse<Speciality>>,
    requestSpecialities: (
        filters: string[]
    ) => Promise<DataResponse<Specialities>>,
    requestMapboxToken: () => Promise<MapBoxTokenResponse>,
    socket: WebSocket
}

export const initializeDataService = (
    onClose?: () => void
): Promise<IDataService> => {
    return new Promise((resolve, reject) => {
        const callback = async (socket: WebSocket) => {
            resolve({
                requestServices: createRequestServices(),
                requestAllServices: createRequestAllServices(),
                requestLocations: createRequestLocations(),
                requestExperts: createRequestExperts(),
                requestExpert: createRequestExpert(),
                requestPremise: createRequestPremise(),
                requestTimesInRange: createRequestTimesInRange(socket), // Still handled by websocketd.
                requestTimesInHourRange:                                // Still handled by websocketd.
                    createDataStoreRequestTimesInHourRange(socket),
                requestBuilding: createRequestBuilding(),
                requestLanguage: createRequestLanguage(),
                populateFilters: createPopulateFilters(),
                requestReservation: createRequestReservation(socket),   // Still handled by websocketd.
                requestReservationCancellation:                         // Still handled by websocketd.
                    createRequestReservationCancellation(socket),
                requestLanguages: createRequestLanguages(),
                requestSpeciality: createRequestSpeciality(),
                requestSpecialities: createRequestSpecialities(),
                requestMapboxToken: createRequestMapBoxToken(),
                socket
            });
        };

        initializeSocket(
            callback,
            createOnmessage(waitingResponses),
            websocketURL,
            reject,
            onClose
        );
    });
};

export const initializeSessionKey = (
    socket: WebSocket,
    callback: (sessionKey: string |  null) => void,
    oldSessionKey?: string | null,
): void => {
    createRequestSessionKey(
        socket,
        callback,
        waitingResponses,
        oldSessionKey
    )();
};